/**
 * Evidence-rich proposals — pure helpers for the worker wire.
 *
 * The real-Claude worker's `emit_reply` actions may carry two extra,
 * optional explanation fields — `rationale` (one sentence why) and
 * `evidence` (concrete data lines) — plus, for `update_brain`, a
 * model-claimed `confidence`. None of those extras are part of the typed
 * `AgentAction` union, so this module is the single client-side seam that:
 *
 *   • {@link extractActionWhy} lifts `{ rationale, evidence }` off an
 *     untrusted raw action so it can ride on the queued proposal
 *     (`ApprovalItem.rationale` / `.evidence`) and render as the inbox's
 *     "Why?" section;
 *
 *   • {@link sanitizeWorkerAction} returns the action with those wire-only
 *     keys stripped, and stamps worker `update_brain` proposals with
 *     `source: 'inferred'` — the Brain must honestly record that a model,
 *     not a human, surfaced the fact (the executor caps inferred
 *     confidence at 0.8). A worker evidence array is folded into the
 *     fact's single `evidence` string when the action didn't carry one.
 *
 * Everything here is pure — the worker still only PROPOSES; writes stay
 * behind the approval/confirm gates.
 */

import type { AgentAction } from './types';

/** Optional proposer-supplied explanation attached to a queued proposal. */
export interface ProposalWhy {
  rationale?: string;
  evidence?: string[];
}

/**
 * Pull `{ rationale, evidence }` off an untrusted raw action object.
 * Returns null when neither field is present/valid — callers attach
 * nothing and older proposals keep working.
 */
export function extractActionWhy(raw: unknown): ProposalWhy | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as { rationale?: unknown; evidence?: unknown };
  const why: ProposalWhy = {};
  if (typeof r.rationale === 'string' && r.rationale.trim()) {
    why.rationale = r.rationale.trim();
  }
  if (Array.isArray(r.evidence)) {
    const lines = r.evidence.filter(
      (e): e is string => typeof e === 'string' && e.trim().length > 0,
    );
    if (lines.length > 0) why.evidence = lines;
  }
  return why.rationale || why.evidence ? why : null;
}

/**
 * Normalise one worker-emitted action for the typed client pipeline:
 * strip the wire-only `rationale`/`evidence[]` extras, and stamp
 * `update_brain` proposals as `inferred` (folding the evidence lines into
 * the fact's single evidence string when it has none). All other action
 * kinds pass through untouched apart from the stripped extras.
 */
export function sanitizeWorkerAction(action: AgentAction): AgentAction {
  const why = extractActionWhy(action);
  // Shallow copy without the wire-only keys.
  const { rationale: _r, evidence: _e, ...rest } = action as AgentAction & {
    rationale?: unknown;
    evidence?: unknown;
  };
  void _r;
  void _e;
  const clean = rest as AgentAction;
  if (clean.kind !== 'update_brain') return clean;
  return {
    ...clean,
    source: 'inferred',
    // A Brain fact carries one evidence string; keep an explicit string if
    // the worker set one, else fold the evidence lines into it.
    evidence:
      typeof (action as { evidence?: unknown }).evidence === 'string'
        ? ((action as { evidence?: string }).evidence as string)
        : why?.evidence?.join('; '),
  };
}
